/**
 * Межа першого екрана (performance-budget.md, розд. 3; docs/dev/web-speed.md): після неї — те, що
 * першому кадру не потрібне. Запити першого екрана після входу — лише документ, код першого екрана,
 * `/meta`, налаштування людини і дані стартового екрана; решта (`/me`, профіль компанії, лічильник
 * подання, RUM, у легкому режимі — потік подій) чекає тут, щоб не забирати в нього мережу й процесор.
 *
 * Межа — перший намальований маршрут: роутер дочекався даних маршруту (`loader`) і показав його
 * (`onRendered`), кадр намальовано, потім простій. Так однаково для будь-якого стартового екрана:
 * список, порожній список, картка за посиланням, адмінка, сторінка помилки. Якщо маршрут так і не
 * намалювався (завис `loader`), межу ставить запобіжник `FIRST_SCREEN_CAP_MS`.
 *
 * Порядок на межі: спершу планувальник простою (`perf/prefetch.ts` ставить мітку `prefetch:start`),
 * потім завдання `afterFirstScreen`, потім підписники React (`useFirstScreenDone`). Тож у замірі
 * звичайного режиму все відкладене йде вже після `prefetch:start`, а в легкому — після
 * `cl:list-ready`.
 */
import { useSyncExternalStore } from 'react';

/** Запобіжник: межа не пізніше ніж через стільки після старту, навіть якщо маршрут не намалювався. */
export const FIRST_SCREEN_CAP_MS = 5_000;
/** Простій після першого кадру — не довше за цей час (`requestIdleCallback`, `timeout`). */
export const FIRST_SCREEN_IDLE_TIMEOUT_MS = 1_000;

/** Середовище браузера — підмінне в тестах. */
export interface FirstScreenEnv {
  /** Після того, як кадр намальовано (rAF, потім повідомлення). */
  afterPaint(cb: () => void): void;
  /** У простої, не пізніше `FIRST_SCREEN_IDLE_TIMEOUT_MS`. */
  idle(cb: () => void): void;
  /** Таймер; повертає скасування. */
  timer(cb: () => void, ms: number): () => void;
}

export const browserFirstScreenEnv: FirstScreenEnv = {
  afterPaint: (cb) => {
    requestAnimationFrame(() => {
      const channel = new MessageChannel();
      channel.port1.onmessage = () => cb();
      channel.port2.postMessage(0);
    });
  },
  idle: (cb) => {
    if (window.requestIdleCallback)
      window.requestIdleCallback(cb, { timeout: FIRST_SCREEN_IDLE_TIMEOUT_MS });
    else window.setTimeout(cb, 1);
  },
  timer: (cb, ms) => {
    const id = window.setTimeout(cb, ms);
    return () => window.clearTimeout(id);
  },
};

type State = 'waiting' | 'rendered' | 'done';
let state: State = 'waiting';
/** Планувальник простою: на межі — першим (мітка `prefetch:start` раніше за відкладене). */
const boundary = new Set<() => void>();
const tasks = new Set<() => void>();
const listeners = new Set<() => void>();

function settle() {
  if (state === 'done') return;
  state = 'done';
  for (const run of [...boundary, ...tasks]) {
    try {
      run();
    } catch {
      // Збій одного завдання не зупиняє решту.
    }
  }
  boundary.clear();
  tasks.clear();
  for (const listener of listeners) listener();
}

function enqueue(set: Set<() => void>, task: () => void): () => void {
  if (state === 'done') {
    task();
    return () => undefined;
  }
  set.add(task);
  return () => set.delete(task);
}

/** Виконати після межі першого екрана (одразу, якщо її вже пройдено). Повертає скасування. */
export function afterFirstScreen(task: () => void): () => void {
  return enqueue(tasks, task);
}

/** Лише для планувальника простою (`perf/prefetch.ts`): на межі — раніше за `afterFirstScreen`. */
export function onFirstScreenBoundary(task: () => void): () => void {
  return enqueue(boundary, task);
}

export const isFirstScreenDone = (): boolean => state === 'done';

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Межу пройдено: компоненти вмикають відкладені запити (`enabled`). */
export function useFirstScreenDone(): boolean {
  return useSyncExternalStore(subscribe, isFirstScreenDone, isFirstScreenDone);
}

/** Перший маршрут намальовано: далі кадр і простій — і межа. Повторні виклики нічого не роблять. */
export function firstScreenRendered(env: FirstScreenEnv = browserFirstScreenEnv): void {
  if (state !== 'waiting') return;
  state = 'rendered';
  env.afterPaint(() => env.idle(settle));
}

interface RenderedSource {
  subscribe(event: 'onRendered', cb: () => void): () => void;
}

/** Старт застосунку (`main.tsx`): межа за першим `onRendered` роутера або за запобіжником. */
export function watchFirstScreen(
  router: RenderedSource,
  env: FirstScreenEnv = browserFirstScreenEnv,
): () => void {
  let off: (() => void) | undefined;
  const cancelCap = env.timer(() => {
    off?.();
    settle();
  }, FIRST_SCREEN_CAP_MS);
  off = router.subscribe('onRendered', () => {
    off?.();
    cancelCap();
    firstScreenRendered(env);
  });
  return () => {
    off?.();
    cancelCap();
  };
}

/** Лише для тестів: свіжий стан (`done` — межу вже пройдено, як після першого екрана). */
export function resetFirstScreenForTests(done = false): void {
  state = done ? 'done' : 'waiting';
  boundary.clear();
  tasks.clear();
  for (const listener of listeners) listener();
}
